import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../api";

// Who has scored what, for a whole event. Answers the question that used to
// mean opening nominations one by one: which juror is holding up the result?
export default function AdminCoveragePage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    api.get(`/jury/coverage/event/${eventId}`)
      .then(res => setData(res.data.data))
      .catch(() => setLoadErr(true));
  }, [eventId]);

  const copyEmails = (rows) => {
    const emails = rows.map(r => r.email).filter(Boolean).join(", ");
    if (!emails) return;
    navigator.clipboard?.writeText(emails);
  };

  if (loadErr) return (
    <Layout title="Panel Coverage">
      <BackButton onClick={() => navigate(-1)} />
      <div style={{ ...card, background: "#FEF2F2", border: "1px solid #FECACA", textAlign: "center" }}>
        <p style={{ fontWeight: 700, color: "#DC2626", margin: "0 0 6px" }}>Could not load coverage</p>
        <p style={{ color: "#94A3B8", fontSize: 13, margin: 0 }}>Go back and try again.</p>
      </div>
    </Layout>
  );

  if (!data) return (
    <Layout title="Panel Coverage">
      <BackButton onClick={() => navigate(-1)} />
      <div style={card}>
        <div className="skeleton" style={{ height: 14, width: "40%", marginBottom: 10 }} />
        <div className="skeleton" style={{ height: 10, width: "60%" }} />
      </div>
    </Layout>
  );

  const { totalNominations, jurors } = data;
  const behind = jurors.filter(j => j.isActive && j.pending > 0);
  const done = jurors.filter(j => j.isActive && j.pending === 0 && j.expected > 0);
  const panelScored = jurors.reduce((s, j) => s + j.scored, 0);
  const panelExpected = jurors.reduce((s, j) => s + j.expected, 0);
  const panelPct = panelExpected > 0 ? Math.round((panelScored / panelExpected) * 100) : 0;

  return (
    <Layout title="Panel Coverage" subtitle="Scoring progress for every juror on this event">
      <BackButton onClick={() => navigate(-1)} />

      {/* Headline */}
      <div className="voh-stats" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
        <Stat label="Nominations with jury" value={totalNominations} />
        <Stat label="Jurors on panel" value={jurors.length} />
        <Stat label="Scores collected" value={`${panelScored}/${panelExpected}`} />
        <Stat label="Panel complete" value={`${panelPct}%`} accent={panelPct === 100 ? "#059669" : "#B45309"} />
      </div>

      {/* Chase list */}
      {behind.length > 0 && (
        <div style={{ ...card, borderLeft: "3px solid #F59E0B", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
            <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: "#0F172A" }}>
              {behind.length} juror{behind.length === 1 ? "" : "s"} still scoring
            </h3>
            <button onClick={() => copyEmails(behind)}
              style={{ marginLeft: "auto", background: "#F59E0B", color: "#fff", border: "none", borderRadius: 7, padding: "6px 13px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              Copy their emails
            </button>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "#64748B" }}>
            Paste into your mail client to send a reminder. {done.length} juror{done.length === 1 ? " has" : "s have"} finished.
          </p>
        </div>
      )}

      {/* Juror table */}
      <div className="voh-table-wrap" style={{ overflowX: "auto", background: "#fff", borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
          <thead>
            <tr style={{ background: "#F8FAFC" }}>
              {["Juror", "Progress", "Scored", "Pending", "Recused", "Last scored"].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {jurors.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ ...td, textAlign: "center", color: "#94A3B8", padding: 40 }}>
                  No jurors assigned to this event yet.
                </td>
              </tr>
            ) : jurors.map((j, idx) => {
              const pct = j.expected > 0 ? Math.round((j.scored / j.expected) * 100) : 100;
              const complete = j.pending === 0;
              return (
                <tr key={j.juryId} style={{ borderTop: idx > 0 ? "1px solid #F1F5F9" : "none", opacity: j.isActive ? 1 : 0.55 }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: "#0F172A", fontSize: 13.5 }}>
                      {j.name}
                      {!j.isActive && (
                        <span style={{ marginLeft: 7, fontSize: 10.5, fontWeight: 700, background: "#F1F5F9", color: "#94A3B8", padding: "2px 7px", borderRadius: 20 }}>
                          Deactivated
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#94A3B8" }}>{j.email}</div>
                  </td>
                  <td style={{ ...td, minWidth: 150 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                      <div style={{ flex: 1, background: "#F1F5F9", borderRadius: 4, height: 6, overflow: "hidden", minWidth: 70 }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: complete ? "#10B981" : "#F59E0B", borderRadius: 4 }} />
                      </div>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: complete ? "#059669" : "#B45309", fontVariantNumeric: "tabular-nums" }}>
                        {pct}%
                      </span>
                    </div>
                  </td>
                  <td style={{ ...td, fontWeight: 700, color: "#0F172A" }}>{j.scored}</td>
                  <td style={td}>
                    {j.pending > 0
                      ? <span style={{ fontSize: 12, fontWeight: 700, background: "#FFFBEB", color: "#D97706", padding: "3px 9px", borderRadius: 8 }}>{j.pending}</span>
                      : <span style={{ fontSize: 12, fontWeight: 700, background: "#ECFDF5", color: "#059669", padding: "3px 9px", borderRadius: 8 }}>Done</span>}
                  </td>
                  <td style={td}>
                    {j.recused > 0
                      ? <span style={{ fontSize: 12, fontWeight: 600, color: "#64748B" }}>{j.recused}</span>
                      : <span style={{ color: "#CBD5E1" }}>—</span>}
                  </td>
                  <td style={{ ...td, fontSize: 12.5, color: "#64748B" }}>
                    {j.lastScoredAt
                      ? new Date(j.lastScoredAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}

const BackButton = ({ onClick }) => (
  <button onClick={onClick}
    style={{ marginBottom: 20, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "7px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#475569", fontFamily: "inherit" }}>
    ← Back
  </button>
);

const Stat = ({ label, value, accent }) => (
  <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, padding: "16px 18px" }}>
    <p style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 800, color: accent || "#0F172A", fontVariantNumeric: "tabular-nums" }}>{value}</p>
    <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</p>
  </div>
);

const card = { background: "#fff", borderRadius: 12, padding: 20, boxShadow: "var(--shadow-sm)", border: "1px solid #E2E8F0" };
const th = { padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const td = { padding: "12px 16px", fontSize: 13.5, color: "#333", verticalAlign: "middle" };
